// Redux Toolkit slice for the Tenants page. Holds the loaded list, derived
// stats, async status, and the list-screen filter/search UI state.

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { ITenant, ITenantStats, TenantStatusFilter } from "../models/interfaces";
import { getTenants, getValidationConfig } from "../serviceHelper";
import { computeTenantStats, transformTenantsResponse } from "../transformer";

interface TenantsState {
	tenants: ITenant[];
	stats: ITenantStats;
	isLoading: boolean;
	error: string | null;
	statusFilter: TenantStatusFilter;
	search: string;
	// Onboard modal visibility lives here so the global top bar can open it while
	// the modal itself is rendered by the Tenants page.
	onboardOpen: boolean;
	// Allowed POC email domains, mirrored from the backend
	// (GET /tenants/validation-config) so the FE email rule can never drift from
	// the server-side one. Empty = not loaded / check disabled; the backend
	// still enforces on submit.
	allowedPocDomains: string[];
	validationConfigLoaded: boolean;
}

export const initialState: TenantsState = {
	tenants: [],
	stats: { active: 0, expiring: 0, decommissioned: 0, total: 0 },
	isLoading: false,
	error: null,
	statusFilter: "all",
	search: "",
	onboardOpen: false,
	allowedPocDomains: [],
	validationConfigLoaded: false,
};

// Fetch the tenant list from the live backend. On failure the thunk rejects and
// the screen surfaces an error (no silent mock fallback).
export const fetchTenants = createAsyncThunk("tenants/fetchTenants", async () => {
	const response = await getTenants();
	return transformTenantsResponse(response);
});

// Fetch the server-side validation rules the FE mirrors (allowed POC domains).
// Fired by the consumers that validate (Onboard modal, Overview edit); the
// `validationConfigLoaded` flag makes repeat dispatches a no-op fetch guard.
export const fetchValidationConfig = createAsyncThunk("tenants/fetchValidationConfig", async () => {
	return await getValidationConfig();
});

const tenantsSlice = createSlice({
	name: "tenantsSlice",
	initialState,
	reducers: {
		setStatusFilter: (state, action: PayloadAction<TenantStatusFilter>) => {
			state.statusFilter = action.payload;
		},
		setSearch: (state, action: PayloadAction<string>) => {
			state.search = action.payload;
		},
		setOnboardOpen: (state, action: PayloadAction<boolean>) => {
			state.onboardOpen = action.payload;
		},
		resetTenants: () => initialState,
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchTenants.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchTenants.fulfilled, (state, action) => {
				state.isLoading = false;
				state.error = null;
				state.tenants = action.payload;
				state.stats = computeTenantStats(action.payload);
			})
			.addCase(fetchTenants.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message ?? "Failed to load tenants";
				// Keep previously loaded tenants: a transient refetch failure (e.g.
				// the refresh after a lifecycle action) must not collapse screens
				// that are already showing data — the detail page used to fall into
				// a permanent skeleton (arch review P1-8). Stats stay consistent
				// with whatever list is kept.
				if (state.tenants.length === 0) {
					state.stats = { active: 0, expiring: 0, decommissioned: 0, total: 0 };
				}
			})
			.addCase(fetchValidationConfig.fulfilled, (state, action) => {
				state.allowedPocDomains = action.payload?.allowed_poc_domains ?? [];
				state.validationConfigLoaded = true;
			})
			.addCase(fetchValidationConfig.rejected, (state) => {
				// Non-fatal: with no list the FE domain rule disables itself and the
				// backend still enforces on submit. Left un-"loaded" so a later
				// consumer retries.
				state.allowedPocDomains = [];
			});
	},
});

export const { resetTenants, setOnboardOpen, setSearch, setStatusFilter } = tenantsSlice.actions;
export default tenantsSlice.reducer;
